import { Location } from './../common/react/Location';
import { FlowPlaceCtrl } from './controllers/FlowPlaceCtrl';
import { FlowPublishResultCtrl } from './controllers/FlowPublishResultCtrl';
import { FeatureFlags } from './FeatureFlags';
import { Filter } from './helpers/Filter';
import { Helpers } from './helpers/Helpers';
import { FlowContentType } from './models/ContentTypes';
import { FlowListItemInfo } from './models/FlowInfo';
import { FlowPlace } from './models/FlowPlace';
import { Module } from './Module';
import { Favorites } from './resources/Favorites';
import { Flows } from './resources/Flows';
import { Projects } from './resources/Projects';
import { ContentUrlForItem } from './services/ContentUrl';
import { ErrorState } from './services/ErrorState';
import { SiteUrlMatcherFactory } from './services/SiteUrlMatcherFactory';
import { StartPageService } from './services/StartPageService';

interface IDetailContentParams extends ng.ui.IStateParamsService {
  id: string;
}

interface ISiteStateParams extends ng.ui.IStateParamsService {
  siteUrlName: string;
}

Module.instance().config(<any[]>[
  '$stateProvider',
  '$urlRouterProvider',
  'SiteUrlMatcherFactoryProvider',
  (
    $stateProvider: ng.ui.IStateProvider,
    $urlRouterProvider: ng.ui.IUrlRouterProvider,
    siteUrlMatcherFactory: SiteUrlMatcherFactory) => {
    const siteUrl = (url: string) => siteUrlMatcherFactory.compile(url);

    /**
     * This function creates a redirect handler for a site url. See $urlRouterProvider documentation for details.
     * The general use case for this function is to redirect differently based on feature flags.
     */
    function customSiteUrlRedirect(getRedirectedUrl: ($match: any) => string | false) {
      return [ '$match', ($match: any) => {
        const redirectedUrl = getRedirectedUrl($match);
        // If the redirected URL is false, don't redirect
        if (redirectedUrl === false) {
          return false;
        }
        return siteUrl(redirectedUrl).format($match);
      }];
    }

    /**
     * A simple URL redirect handler, which redirects to a static URL.
     */
    function siteUrlRedirect(url: string) {
      return customSiteUrlRedirect(() => url);
    }

    const rootSiteUrlRedirect =
    ['$match',
    'StartPageService',
    '$location',
      ($match: ISiteStateParams,
        startPageService: StartPageService,
        $location: ng.ILocationService) => {
        const noSiteInUrl = !$match.siteUrlName;
        const params = Helpers.serialize($location.search());
        const startPage = (noSiteInUrl)
            // When going to the root url ('/') go to the start page for the site we are currently on.
            ? startPageService.getCurrentUserStartPageForCurrentSite()
            // When going to the root url for an explicit site ('/site/foo'), go to the start page on that site
            : startPageService.getCurrentUserStartPageForSite($match.siteUrlName);
        return startPage + (params ? (/\?/.test(startPage) ? '&' : '?') + params : '');
      }
    ];

    // Redirects for default related content types
    // TODO: perhaps add these as part of state creation helper methods
    $urlRouterProvider
      .when(siteUrl(''), rootSiteUrlRedirect)
      .when(siteUrl('/'), rootSiteUrlRedirect)
      .when(siteUrl('/{type}/'), siteUrlRedirect('/{type}'))
      .when(siteUrl('/{type}/{id}/'), siteUrlRedirect('/{type}/{id}'))
      .when(siteUrl('/flows/{id}'), siteUrlRedirect('/flows/{id}/overview'));

    $urlRouterProvider
      .when(siteUrl('/redirect_to_flow/{id}'),
        ['$match',                'ErrorState',           'Favorites',          'Flows',
        ($match: any, errorState: ErrorState, favorites: Favorites, flows: Flows) => {
        flows.getById($match.id).then(
          () => {
            const url = ContentUrlForItem({id: $match.id}, FlowContentType);
            Location.instance.setUrl(url);
          },
          () => {
            favorites.remove($match.id, 'flow');
            errorState.go({ detailsLocKey: 'favoriteFlowCouldNotBeFound' });
          }
        );
      }]);

    $stateProvider
      .state('main.site.content', {
        url: '',
        template: '<div class="tb-fill" ui-view></div>',
        abstract: true
      });

    // Flow details
    if (!FeatureFlags.flowSpaceReactRouter) {
      $stateProvider.state('main.site.content.flow', {
        url: '/flows/:id',
        template: '<tb:connected-flow-space props="flowSpaceOwnProps"/>',
        abstract: true,
        resolve: {
          flowAllowedActions: ['Flows', '$stateParams', (Flows: Flows, $stateParams: IDetailContentParams) => {
            return Flows.actions($stateParams.id);
          }],
          flowDetailInfo: ['Flows', '$stateParams', (Flows: Flows, $stateParams: IDetailContentParams) => {
            return Flows.getById($stateParams.id);
          }],
          flowListItemInfo: ['Flows', '$stateParams', (Flows: Flows, $stateParams: IDetailContentParams) => {
            return Flows.all()
              .withFilters([Filter.fieldEq('id', $stateParams.id)])
              .getFirst();
          }],
          place: ['flowListItemInfo', 'FlowPlace', (flowListItemInfo: FlowListItemInfo, FlowPlace: FlowPlace) => {
            return FlowPlace.create(flowListItemInfo.id);
          }],
          projectHierarchy: ['flowListItemInfo', 'Projects', (flowListItemInfo: FlowListItemInfo, Projects: Projects) => {
            return Projects.getProjectHierarchy(flowListItemInfo.project.id);
          }]
        },
        controller: FlowPlaceCtrl,
        data: {
          errorState: {
            detailsLocKey: 'flowCannotBeAccessed',
            redirectState: 'main.site.content.site.flows',
            redirectLocKey: 'backToFlows'
          }
        }
      })
      .state('main.site.content.flow.overview', {
        url: '/overview',
        data: {
          subplaceName: 'overview',
        },
      })
      .state('main.site.content.flow.connections', {
        url: '/connections',
        data: {
          subplaceName: 'connections',
        },
      })
      .state('main.site.content.flow.scheduledTasks', {
        url: '/scheduledTasks',
        data: {
          subplaceName: 'scheduledTasks',
        },
      })
      .state('main.site.content.flow.runHistory', {
        url: '/runHistory',
        data: {
          subplaceName: 'runHistory',
        },
      })
      .state('main.site.content.flow.lineage', {
        url: '/lineage',
        data: {
          subplaceName: 'lineage',
        }
      })
      .state('main.site.content.flow.lineage.subpath', {
        url: '/*path',
      })
      .state('main.site.content.flow.publishresult', {
        url: '/publishresult',
        controller: FlowPublishResultCtrl,
        data: {
          subplaceName: 'publishresult',
        },
      });
    }

    // !!! ATTENTION !!!
    // If you changed the url scheme of user or user setting,
    // make sure to also update the user setting url generation code in:
    // \workgroup\vqlweb\scriptsharp\src\Authoring\ViewModels\AuthoringMastheadViewModel.cs

    // Adding an extra child state underneath the abstract one here to
    // avoid remounting of the space router when changing urls that are
    // on this state
    $stateProvider
      .state('main.site.content.reactrouter', {
        template: '<tb:site-space-router/>', // SiteSpaceRouter.tsx via angularDirectives.ts
        abstract: true
      }).state('main.site.content.reactrouter.base', {
        url: siteUrl(`/{space:(?:jobs|dataAlert|search|schedules|explore|flows|workbooks|views|projects|dataRoles|home|metrics|recommendations|redirect_to_view|redirect_to_favorite_view|redirect_to_project|redirect_to_workbook|redirect_to_datasource|redirect_to_removed_datasource|redirect_to_data_role|groups|user|users|favorites|recents|datasources|status|tasks)}`),
      })
      .state('main.site.content.reactrouter.base.subspace', {
        url: '/*subspace'
      });
  }
]);
